import React, { useEffect, useMemo, useState } from "react";

import {
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  SelectChangeEvent,
  styled,
  Tooltip,
  TooltipProps,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { tooltipClasses } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import RadioButton from "Components/Form/RadioGroup";
import { add, isBefore, isSameDay } from "date-fns";
import { format } from "date-fns-tz";
import { Member } from "Models/members/member";
import { useTranslation } from "react-i18next";
import { AiOutlinePlus } from "react-icons/ai";
import { HiChevronDoubleDown, HiChevronDoubleUp } from "react-icons/hi";
import { getEquipmentName } from "Util/getEquipmentName";
import { getMemberDescription } from "Util/getMemberDescription";
import {
  AFTERNOON_END,
  AFTERNOON_START,
  EVENING_END,
  EVENING_START,
  getStartDatePeriods,
  getStartDateRangeLabel,
  MORNING_END,
  MORNING_START,
  TASK_DATE_MINUTES,
} from "Util/startDate";
import * as yup from "yup";

import { Select, Input, AutoSuggest } from "../../../../../../components/Form";
import Button, { Color } from "../../../../../../components/Form/Button";
import ActionButtons from "../../../../../../components/Generic/ActionButtons";
import SkeletonComponent from "../../../../../../components/Generic/Skeleton";
import LoadingModal from "../../../../../../components/LoadingModal";
import { useAppState } from "../../../../../../context/StateContext";
import { Fleet } from "../../../../../../models/fleet";
import { Task, TaskDateProperties, TaskStartDatePeriod } from "../../../../../../models/task";
import {
  AgentStatus,
  Status,
  TaskStatus,
  TaskType,
  TaskTypeRole,
  TimeSlotType,
} from "../../../../../../util/enums";
import sortSequence from "../../../../../../util/sortSequence";
import useStyles, {
  AddTaskButton,
  ExpandAllButtonContent,
  ExpandAllTitle,
  GridItem,
  InfoIcon,
  InfoTitle,
  MenuItemTitle,
  PeriodMenuItem,
  TasksAndMilestonesInfo,
  TooltipBottomText,
  TooltipContentWrapper,
  BoxTimezone,
} from "./styles";
import TaskItem from "./TaskItem";
import { useTaskActions } from "./useTaskActions";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { VariableSlotTime } from "./VariableSlotTime";
import { TimeSlotTask } from "Models/time.slot.task";
import { TaskDateUtils } from "Util/dateUtils";
import { useTaskDateManagement } from "Hooks/useTaskDateManagement";

import { TaskTabProps } from "./utils/types";
import { PERIOD_BUFFER_TIME } from "./utils/enum";
import { getDefaultTaskValue } from "./utils/getDefaultTaskValue";
import { TaskSummary } from "./components/Summary";
import { useQuery } from "@tanstack/react-query";
import AgentService from "Services/agent.service";
import { Agent } from "Models/agent";
import { InfoCircle, SearchNormal1, Share } from "iconsax-react";
import { GrReturn } from "react-icons/gr";
import { DefaultTooltip } from "Components/Generic/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { formatGMT as formatGMTUtil } from "Util/Format";
import { IconTimezoneClock } from "Components/Generic/Icons/IconTimezoneClock";
import { MOVING_AGENT_COMPANY_PROFILE_ID } from "Constants";

const INFO_CIRCLE_ICON = require("Assets/icons/info-circle-2.svg");

const TasksTab = (props: TaskTabProps) => {
  const {
    order,
    tasks,
    taskDates,
    isOnViewMode = false,
    onCancel,
    onSave,
    onSavePublish,
    handleUpdate,
    isUpdating,
  } = props;

  const theme = useTheme();

  const classes = useStyles(isOnViewMode)();

  const { t: translate } = useTranslation();

  const { currentUser, userTimezone, defaultTimezone, agentPermissions } = useAppState();
  const timezone = userTimezone?.name || userTimezone?.nameFormatted || defaultTimezone;

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const [task, setTask] = useState<Task>(getDefaultTaskValue() as Task);

  const [taskTypeSelected, setTaskTypeSelected] = useState<string>("");

  const [expandAll, setExpandAll] = useState<boolean>(false);
  const [slotTime, setSlotTime] = useState<TimeSlotType>(TimeSlotType.NONE);
  const [assignToAgent, setAssignToAgent] = useState<boolean>(false);

  const { isMultiDayTask, handlePeriodChange } = useTaskDateManagement({
    initialTask: task,
    onTaskUpdate: setTask,
  });

  const {
    getAllFleets,
    getAllLeaders,
    getAllStaffs,
    getAllTeams,
    getTaskTypes,
    getAllMembers,
    hasCompanyAndBranchIdEnabled,
  } = useTaskActions(order);

  const { data: agentList } = useQuery({
    queryKey: ["agent.list"],
    queryFn: async () => {
      const response = await AgentService.getAll({
        searchByOr: JSON.stringify([
          { status: AgentStatus.ACTIVE },
          { status: AgentStatus.INVITATION_SENT },
          { status: AgentStatus.INVITATION_EXPIRED },
        ]),
        sortBy: JSON.stringify([["name", "asc"]]),
        createdCompanyId: currentUser?.companyId,
        include: JSON.stringify([
          { association: "associatedCompany", include: [{ association: "profile" }] },
        ]),
      });

      if (response && response.data) return response.data;

      return [] as Agent[];
    },
  });

  const { isFetching: isLoadingMembers, data: members } = getAllMembers();
  const { data: leaders } = getAllLeaders();

  const { isFetching: isLoadingTaskTypeList, data: taskTypeListData } = getTaskTypes();
  const { data: staffs } = getAllStaffs();
  const { isFetching: isLoadingTeams, data: teams } = getAllTeams();
  const { isFetching: isLoadingFleets, data: fleets } = getAllFleets();

  const memberLeaders = useMemo(() => {
    return members?.filter((member) => member.userCompany?.leader);
  }, [members]);

  const sortedTasks = useMemo(() => {
    return tasks?.sort(
      (taskA, taskB) => new Date(taskA.startDate).getTime() - new Date(taskB.startDate).getTime()
    );
  }, [tasks]);

  const tasksTypeList = useMemo(() => {
    if (!taskTypeListData.length) return [];

    return sortSequence(
      taskTypeListData.filter(
        (taskType) =>
          taskType.name !== TaskType.VIDEO_SURVEY && taskType.name !== TaskType.HOME_SURVEY
      )
    );
  }, [taskTypeListData]);

  const taskType = useMemo(() => {
    return tasksTypeList?.find((taskType) => taskType.name === taskTypeSelected);
  }, [tasksTypeList, task]);

  const isTaskTypeGeneral = useMemo(
    () => taskType?.role?.toLowerCase() === TaskTypeRole.GENERAL.toLowerCase(),
    [taskType]
  );

  const startTime = useMemo(() => {
    const startDate = task?.startDate
      ? TaskDateUtils.extractTimeForInput(task.startDate, { timezone })
      : "";

    return startDate;
  }, [task?.startDate]);

  const endTime = useMemo(() => {
    const startDate = task?.endDate
      ? TaskDateUtils.extractTimeForInput(task.endDate, { timezone })
      : "";

    return startDate;
  }, [task?.endDate]);

  const startDateLabel = useMemo(() => {
    const startDate = task?.startDate
      ? TaskDateUtils.extractDateForInput(task.startDate, { timezone })
      : "";

    return startDate;
  }, [task?.startDate]);

  const endDateLabel = useMemo(() => {
    const startDate = task?.endDate
      ? TaskDateUtils.extractDateForInput(task.endDate, { timezone })
      : "";

    return startDate;
  }, [task?.endDate]);

  useEffect(() => {
    if (isSubmitted) validateForm();
  }, [task]);

  useEffect(() => {
    if (isSubmitted === false) clearFields();
  }, [isSubmitted]);

  useEffect(() => {
    if (!task.endDate || !task.startDate) return;

    let taskEndDate = new Date(task.endDate);
    const taskStartDate = new Date(task.startDate);

    if (
      isBefore(taskEndDate, taskStartDate) ||
      ([
        TaskStartDatePeriod.MULTI_DAY,
        TaskStartDatePeriod.VARIABLE_TIME,
        TaskStartDatePeriod.FIXED_TIME,
      ].includes(task?.period!) &&
        isSameDay(taskEndDate, taskStartDate))
    ) {
      if (!task.period) return;

      taskEndDate = new Date(taskStartDate);
      taskEndDate = add(taskEndDate, PERIOD_BUFFER_TIME[task.period as TaskStartDatePeriod]);

      updateTaskField("endDate", taskEndDate.toISOString());
    }
  }, [task.period, task.startDate, task.endDate]);

  const validateForm = async () => {
    try {
      const defaultValidation = {
        isMultiDayTask: yup.boolean().nullable(),
        task: yup.string().required(translate("thisFieldIsRequired")),
        period: yup.number().required(translate("thisFieldIsRequired")),
        timeSlots: yup.array().when("isMultiDayTask", {
          is: true,
          then: yup.array().when("period", {
            is: TaskStartDatePeriod.VARIABLE_TIME,
            then: yup
              .array()
              .min(2, translate("itShouldBeAtLeastTwoDates"))
              .of(
                yup.object().shape({
                  date: yup
                    .string()
                    .required(translate("thisFieldIsRequired"))
                    .typeError(translate("thisFieldIsRequired")),
                  from: yup
                    .string()
                    .required(translate("thisFieldIsRequired"))
                    .typeError(translate("thisFieldIsRequired")),
                  to: yup
                    .string()
                    .required(translate("thisFieldIsRequired"))
                    .test(
                      "end-time-greater-than-start-time",
                      translate("endTimeMustBeGreaterThanStartTime"),
                      function (value) {
                        const { date, from } = this.parent;
                        if (!from || !value) return true;

                        const currentDate = date.split("T")[0];
                        const startTime = new Date(`${currentDate}T${from}`);
                        const endTime = new Date(`${currentDate}T${value}`);

                        return endTime > startTime;
                      }
                    ),
                })
              )
              .typeError(translate("thisFieldIsRequired")),
            otherwise: yup.array().nullable(),
          }),
          otherwise: yup.array().nullable(),
        }),
        startDate: yup.string().when("period", {
          is: (value: TaskStartDatePeriod) =>
            value === TaskStartDatePeriod.MULTI_DAY || value === TaskStartDatePeriod.FIXED_TIME,
          then: yup
            .string()
            .required(translate("thisFieldIsRequired"))
            .typeError(translate("thisFieldIsRequired")),
          otherwise: yup.string().nullable(),
        }),
        endDate: yup.string().when("isMultiDayTask", {
          is: true,
          then: yup.string().when("period", {
            is: (value: TaskStartDatePeriod) => value === TaskStartDatePeriod.FIXED_TIME,
            then: yup
              .string()
              .required(translate("thisFieldIsRequired"))
              .typeError(translate("thisFieldIsRequired")),
            otherwise: yup.string().nullable(),
          }),
          otherwise: yup.string().nullable(),
        }),
        startTime: yup.string().when("period", {
          is: (value: TaskStartDatePeriod) =>
            value === TaskStartDatePeriod.MULTI_DAY || value === TaskStartDatePeriod.FIXED_TIME,
          then: yup
            .string()
            .required(translate("thisFieldIsRequired"))
            .typeError(translate("thisFieldIsRequired")),
          otherwise: yup.string().nullable(),
        }),
        endTime: yup.string().when("period", {
          is: (value: TaskStartDatePeriod) => value === TaskStartDatePeriod.FIXED_TIME,
          then: yup
            .string()
            .required(translate("thisFieldIsRequired"))
            .test(
              "end-time-greater-than-start-time",
              translate("endTimeMustBeGreaterThanStartTime"),
              function (value) {
                const { startDate, startTime: from } = this.parent;
                if (!from || !value) return true;

                const currentDate = format(new Date(startDate), "yyyy-MM-dd");
                const startTime = new Date(`${currentDate}T${from}`);
                const endTime = new Date(`${currentDate}T${value}`);

                return endTime > startTime;
              }
            )
            .typeError(translate("thisFieldIsRequired")),
          otherwise: yup.string().nullable(),
        }),
      };
      const schema = isTaskTypeGeneral
        ? yup.object().shape({
            ...defaultValidation,
            memberLeaderId: yup
              .number()
              .required(translate("thisFieldIsRequired"))
              .typeError(translate("thisFieldIsRequired")),
          })
        : assignToAgent
        ? yup.object().shape({
            ...defaultValidation,
            agentId: yup.number().required(translate("thisFieldIsRequired")),
          })
        : yup.object().shape({
            ...defaultValidation,
          });

      const periodAndTimeSlotsCheck = {
        period: task.period,
        timeSlots: task?.timeSlots,
        isMultiDayTask,
      };

      await schema.validate(
        isTaskTypeGeneral
          ? {
              task: task.name,
              startDate: task.startDate,
              startTime,
              endTime,
              memberLeaderId: task?.memberLeaderId,
              ...periodAndTimeSlotsCheck,
            }
          : assignToAgent
          ? {
              task: task.name,
              startDate: task.startDate,
              endDate: task.endDate,
              startTime,
              endTime,
              memberLeaderId: task?.memberLeaderId,
              agentId: task?.agentId,
              ...periodAndTimeSlotsCheck,
            }
          : {
              task: task.name,
              startDate: task.startDate,
              endDate: task.endDate,
              startTime,
              endTime,
              memberLeaderId: task?.memberLeaderId,
              ...periodAndTimeSlotsCheck,
            },
        { abortEarly: false }
      );

      setErrors([]);

      return true;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        setErrors(err.inner);
        // You can also get a list of just the field names that failed validation
        const invalidFields = err.inner.map((error) => error.path);
        console.error("Invalid fields:", invalidFields);
      }

      return false;
    }
  };

  const clearFields = () => {
    const currentTask = getDefaultTaskValue() as Task;

    setSlotTime(TimeSlotType.NONE);
    setTask(currentTask as Task);
    setTaskTypeSelected("");
    setErrors([]);
    handlePeriodChange({ period: TaskStartDatePeriod.MORNING, timezone });
  };

  const handleAddTask = async () => {
    setIsSubmitted(true);
    const isValid = await validateForm();

    if (!isValid) return;
    setIsSubmitted(false);
    const tasksToUpdate: Task[] = [...(tasks || [])];
    let startDate = task.startDate;
    let endDate = task.endDate;

    if (slotTime === TimeSlotType.FIXED) {
      task.period = TaskStartDatePeriod.FIXED_TIME;
    }

    if (slotTime === TimeSlotType.VARIABLE) {
      task.period = TaskStartDatePeriod.VARIABLE_TIME;
    }

    if (slotTime === TimeSlotType.VARIABLE && task?.timeSlots?.length) {
      const currentTimeSlots = task.timeSlots.sort(
        (a, b) => new Date(a.date!).getTime() - new Date(b.date!).getTime()
      );

      const firstSlot = currentTimeSlots[0];
      const lastSlot = currentTimeSlots[currentTimeSlots.length - 1];

      const startCurrentDate = (firstSlot?.date as string)?.split("T")[0];
      const endCurrentDate = (lastSlot?.date as string)?.split("T")[0];

      startDate = TaskDateUtils.createTaskDateTime(
        { dateInput: startCurrentDate, timeInput: firstSlot?.from },
        timezone
      );
      endDate = TaskDateUtils.createTaskDateTime(
        { dateInput: endCurrentDate, timeInput: lastSlot?.from },
        timezone
      );
    }

    handleUpdate?.([
      {
        ...task,
        type: task.type ?? taskTypeSelected,
        startDate,
        endDate,
        period: task.period,
        team: task.team,
        teams: task.team ? [task.team] : [],
        teamId: task?.teamId,
        leaderId: task?.memberLeader?.userId,
        staffs: task?.staffs,
        members: task?.members,
        memberLeaderId: task?.memberLeaderId,
        memberLeader: task?.memberLeader,
        fleets: task?.fleets,
        status: isTaskTypeGeneral ? TaskStatus.TO_DO : TaskStatus.REGISTERED,
        companyId: currentUser?.companyId,
        userId: task?.leaderId!,
        managerId: task?.leaderId!,
        milestoneEnabled: taskType?.milestoneEnabledDefault || false,
        history: `Task ${taskTypeSelected} has been created`,
      } as Task,
      ...tasksToUpdate,
    ]);

    updateTaskField("name", "");
    updateTaskField("type", "");
    setTaskTypeSelected("");

    updateTaskField("memberLeader", undefined);
    updateTaskField("memberLeaderId", undefined);
    updateTaskField("members", undefined);
    updateTaskField("fleets", undefined);
    updateTaskField("team", undefined);
    updateTaskField("teamId", undefined);
    updateTaskField("agent", undefined);
    updateTaskField("agentId", undefined);
    updateTaskField("additionalInstructions", "");

    NotificationToast.success(translate("taskAddedSuccessfully"));
  };

  const onUpdate = (task: Task, index: number): void => {
    const tasksToUpdate: Task[] = [...(tasks || [])];

    tasksToUpdate[index] = {
      ...tasksToUpdate[index],
      ...task,
    };
    handleUpdate?.(tasksToUpdate);
  };

  const fieldHasError = (field: string) => !!errors?.find((error) => error.path === field);

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const taskTypeSequenceNumber = (task: Task) => {
    if (tasks?.length) {
      const tasksSameTypeArray = tasks.filter((taskFiltered) => taskFiltered.type === task.type);

      if (tasksSameTypeArray.length > 1) {
        return tasksSameTypeArray.indexOf(task) + 1;
      }
    }
  };

  const MilestonesInfoTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
      padding: "12px 16px",
      maxWidth: 312,
      background: colors.white,
      font: "400 14px Manrope",
      color: colors.gray600,
      lineHeight: "20px",
      letterSpacing: "0.25px",
      borderRadius: "12px",
      boxShadow: "0px 1px 3px 1px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.30)",
    },
  }));

  const renderExpandTasks = () => {
    return (
      <section className={classes.expandAllWrapper}>
        <TasksAndMilestonesInfo>
          <ExpandAllTitle>{translate("tasks")}</ExpandAllTitle>
        </TasksAndMilestonesInfo>

        <Button
          customColor={Color.TEXT_INFO}
          className={classes.expandAllButton}
          onClick={() => setExpandAll((state) => !state)}
        >
          <ExpandAllButtonContent>
            <span>{expandAll ? translate("hideAllTasks") : translate("expandAllTasks")}</span>
            {expandAll ? <HiChevronDoubleUp /> : <HiChevronDoubleDown />}
          </ExpandAllButtonContent>
        </Button>
      </section>
    );
  };

  const defaultTimePeriods = useMemo(() => getStartDatePeriods(new Date()), []);

  const renderTasks = () => {
    if (!sortedTasks?.length) return null;

    return (
      <div>
        {!isOnViewMode && renderExpandTasks()}
        {sortedTasks.map((task, index: number) => {
          if ([TaskType.VIDEO_SURVEY, TaskType.HOME_SURVEY].includes(task?.type as TaskType))
            return null;

          return (
            <TaskItem
              key={index}
              task={task}
              memberLeaders={memberLeaders}
              leaders={leaders}
              staffs={staffs}
              fleets={fleets}
              teams={teams}
              members={members}
              expandAll={expandAll}
              taskTypeSequence={taskTypeSequenceNumber(task)}
              isOnViewMode={isOnViewMode}
              onUpdate={(task: Task) => onUpdate(task, index)}
              tasksTypesList={tasksTypeList}
              isLoadingData={{
                isLoadingTaskTypeList,
                isLoadingTeams,
                isLoadingMembers,
                isLoadingFleets: isLoadingFleets && hasCompanyAndBranchIdEnabled,
              }}
            />
          );
        })}
      </div>
    );
  };

  const getDateRangeValue = (value: number) => {
    switch (value) {
      case TaskStartDatePeriod.MORNING:
        return getStartDateRangeLabel(defaultTimePeriods.morning);
      case TaskStartDatePeriod.AFTERNOON:
        return getStartDateRangeLabel(defaultTimePeriods.afternoon);
      case TaskStartDatePeriod.EVENING:
        return getStartDateRangeLabel(defaultTimePeriods.evening, { considerEvening: true });
      case TaskStartDatePeriod.FULL_DAY:
        return getStartDateRangeLabel(defaultTimePeriods.fullDay, { isFullDay: true });
      default:
        return translate("custom");
    }
  };

  const renderAssignToAgentRadio = () => {
    return (
      <RadioButton
        label={translate("assignToAnAgent")}
        onChange={(event) => {
          setAssignToAgent(event.target.value == "true" ? true : false);
          setIsSubmitted(false);
          if (event.target.value == "true") {
            updateTaskField("memberLeader", undefined);
            updateTaskField("memberLeaderId", undefined);
            updateTaskField("members", undefined);
            updateTaskField("fleets", undefined);
            updateTaskField("team", undefined);
            updateTaskField("teamId", undefined);
          } else {
            updateTaskField("agent", undefined);
            updateTaskField("agentId", undefined);
            updateTaskField("additionalInstructions", "");
          }
        }}
        options={[
          {
            label: translate("yes"),
            value: true,
          },
          {
            label: translate("no"),
            value: false,
          },
        ]}
        value={assignToAgent}
      />
    );
  };

  const renderMultidayRadio = () => {
    return (
      <RadioButton
        label={translate("isThisAMultiDayTask")}
        onChange={(event) => {
          setSlotTime(TimeSlotType.NONE);
          setTask((prev) => ({ ...(prev || {}), timeSlots: [] } as Task));
          const rawValue = event.target.value;

          const parsedValue = rawValue.includes(",")
            ? rawValue.split(",").map((value) => Number(value.trim()))
            : Number(rawValue);

          handlePeriodChange({
            period:
              (Array.isArray(parsedValue) ? parsedValue.at(0) : parsedValue) ??
              TaskStartDatePeriod.MORNING,
            timezone,
          });
        }}
        options={[
          {
            label: translate("yes"),
            value: [
              TaskStartDatePeriod.MULTI_DAY,
              TaskStartDatePeriod.VARIABLE_TIME,
              TaskStartDatePeriod.FIXED_TIME,
            ],
          },
          {
            label: translate("no"),
            value: [
              TaskStartDatePeriod.MORNING,
              TaskStartDatePeriod.AFTERNOON,
              TaskStartDatePeriod.EVENING,
              TaskStartDatePeriod.FULL_DAY,
              TaskStartDatePeriod.CUSTOM,
            ],
          },
        ]}
        value={task.period}
      />
    );
  };

  const updateTaskField = <T extends keyof Task>(key: T, value: Task[T]) => {
    setTask((prevTask) => ({ ...prevTask, [key]: value }));
  };

  const leaderInputValue = useMemo(() => {
    if (task?.memberLeader) {
      return getMemberDescription(task.memberLeader, { useEmail: true });
    }
    return "";
  }, [task?.memberLeader]);

  const handleChangeDateTime = (key: TaskDateProperties, value: string) => {
    const existingTime = task[key]
      ? TaskDateUtils.extractTimeForInput(task[key]!, { timezone })
      : undefined;

    const date = TaskDateUtils.createTaskDateTime(
      {
        dateInput: value,
        timeInput: existingTime,
        preserveExistingTime: true,
        existingDateString: task[key],
      },
      timezone
    );

    updateTaskField(key, date);
  };

  const handleChangeTime = (key: TaskDateProperties, value: string) => {
    const existentDate = task.startDate
      ? TaskDateUtils.extractDateForInput(task[key] ?? task.startDate, { timezone })
      : TaskDateUtils.getTodayForInput();
    const date = TaskDateUtils.createTaskDateTime(
      { dateInput: existentDate, timeInput: value },
      timezone
    );

    updateTaskField(key, date);
  };

  const handleChangeTeam = (teamId: number) => {
    const teamSelected = teams.find((team) => team.id === teamId);

    if (teamSelected) {
      const leader = leaders?.find((leader) => leader.id === teamSelected?.memberLeader?.userId);
      const teamStaff =
        staffs?.filter(
          (staff) =>
            teamSelected?.members
              ?.filter((member) => !!member.staffId)
              .map((member) => member.staffId)
              .includes(staff.id) && staff.id !== teamSelected?.memberLeader?.userId
        ) ?? [];
      const teamMembers =
        teamSelected?.members?.filter((member) => member.id !== teamSelected?.memberLeader?.id) ??
        [];

      const teamFleets = teamSelected?.fleets || [];
      const memberLeader = teamSelected?.memberLeader;

      updateTaskField("leader", leader);
      updateTaskField("leaderId", leader?.id);
      updateTaskField("staffs", teamStaff);
      updateTaskField("members", teamMembers);
      updateTaskField("memberLeader", memberLeader);
      updateTaskField("memberLeaderId", memberLeader?.id);
      updateTaskField("fleets", teamFleets);
    }

    updateTaskField("team", teamSelected);
    updateTaskField("teamId", teamId);
    updateTaskField("rootTeamId", teamSelected?.rootTeamId || teamId);
  };

  const displayTimezone = () => {
    if (!userTimezone && !!defaultTimezone?.trim()?.length) {
      return <span>{defaultTimezone}</span>;
    }

    const currentTimezone = userTimezone;

    if (!currentTimezone?.gmtOffset || !currentTimezone?.nameFormatted) {
      return (
        <span>{`${currentTimezone?.name || "Unknown"} (GMT ${
          currentTimezone?.gmtOffset || 0
        })`}</span>
      );
    }

    return (
      <span>{`GMT (${formatGMTUtil(currentTimezone.gmtOffset)}) ${currentTimezone.nameFormatted
        ?.replace("/", " / ")
        .replace("_", "")}`}</span>
    );
  };

  return (
    <div data-cy="tasksComponentBox">
      <div className={classes.container}>
        {!isOnViewMode && (
          <BoxTimezone>
            <IconTimezoneClock color={colors.gray500} />
            {displayTimezone()}
          </BoxTimezone>
        )}

        <TaskSummary
          tasks={tasks}
          taskDates={taskDates}
          isViewMode={isOnViewMode}
          onUpdate={handleUpdate}
        />
        {isOnViewMode && renderExpandTasks()}

        {!isOnViewMode &&
          (currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
            agentPermissions?.[Number(order?.id)]?.order?.sections?.task?.fields?.general
              ?.addNew === true) && (
            <section data-cy="bodyFormComponent" className={classes.addTaskWrapper}>
              <header>
                <span className={classes.title}>{translate("addATask")}</span>
              </header>
              <Grid container direction="row" spacing={2}>
                <Grid item container xs={12} spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <SkeletonComponent isLoading={isLoadingTaskTypeList} isInput>
                      <Select
                        required
                        data-cy="formSelectTypeTask"
                        name="task"
                        label={translate("task")}
                        value={task.type ?? ""}
                        displayEmpty
                        defaultValue=""
                        onChange={(event: SelectChangeEvent<any>) => {
                          updateTaskField("type", event.target.value);
                          updateTaskField("name", event.target.value);
                          setTaskTypeSelected(event.target.value);
                        }}
                        error={fieldHasError("task")}
                        errorMessage={getErrorByField("task")}
                        placeholder={translate("select")}
                      >
                        {tasksTypeList.map((type, index: number) => (
                          <MenuItem key={`type-${index}`} value={type.name}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </SkeletonComponent>
                  </Grid>
                  <Grid
                    item
                    xs="auto"
                    sx={{
                      flexGrow: 0,
                      flexShrink: 0,
                    }}
                  >
                    {renderAssignToAgentRadio()}
                  </Grid>
                  <Grid
                    item
                    xs="auto"
                    sx={{
                      display: { xs: "none", sm: "flex" },
                      alignItems: "stretch",
                    }}
                  >
                    <Divider orientation="vertical" flexItem />
                  </Grid>
                  <Grid
                    item
                    xs="auto"
                    sx={{
                      flexGrow: 0,
                      flexShrink: 0,
                    }}
                  >
                    {renderMultidayRadio()}
                  </Grid>
                  {assignToAgent && (
                    <React.Fragment>
                      <Grid item container xs={12} spacing={2}>
                        <Grid item xs={12} md={3}>
                          <AutoSuggest
                            required
                            label={translate("selectAnAgent")}
                            placeholder={translate("select")}
                            options={agentList}
                            value={task?.agent || undefined}
                            getOptionLabel={(option) => option.entity}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(value: Agent) => {
                              updateTaskField("agent", value);
                              updateTaskField("agentId", value?.id);
                            }}
                            renderOption={(props, option, { selected }) => (
                              <li
                                {...props}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: "12px",
                                }}
                                key={option.id || option.entity}
                              >
                                <span
                                  style={{
                                    flexShrink: 1,
                                  }}
                                >
                                  {option?.entity}
                                </span>
                                {option.isShipeeziNetwork && (
                                  <span style={{ flexShrink: 0 }}>
                                    <Share size={20} color="#308CDB" />
                                  </span>
                                )}
                              </li>
                            )}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchNormal1 color="#98A2B3" />
                                </InputAdornment>
                              ),
                            }}
                            error={fieldHasError("agentId")}
                            errorMessage={getErrorByField("agentId")}
                          />
                        </Grid>
                        <Grid item xs={12} md={9}>
                          <Input
                            type="text"
                            label={translate("additionalInstructions")}
                            placeholder={translate("enterAdditionalInstructions")}
                            value={task.additionalInstructions}
                            onChange={(event) =>
                              updateTaskField("additionalInstructions", event.target.value)
                            }
                            error={fieldHasError("startTime")}
                            errorMessage={getErrorByField("startTime")}
                            infoMessage={translate(
                              "thisInstructionWillBeVisibleInTheEmailAndTaskView"
                            )}
                          />
                        </Grid>
                      </Grid>
                    </React.Fragment>
                  )}
                  {isMultiDayTask && (
                    <Grid item xs={4}>
                      <RadioButton
                        label={translate("isTimeSlotRequiredForMultiDayTask")}
                        onChange={(event) => {
                          if (event.target.value === TimeSlotType.VARIABLE) {
                            setTask((prev) => ({
                              ...(prev || {}),
                              period: TaskStartDatePeriod.VARIABLE_TIME,
                              timeSlots: [{ date: new Date().toISOString() } as TimeSlotTask],
                            }));
                          } else {
                            const taskPeriod =
                              event.target.value === TimeSlotType.FIXED
                                ? TaskStartDatePeriod.FIXED_TIME
                                : TaskStartDatePeriod.MULTI_DAY;
                            setTask((prev) => ({
                              ...(prev || {}),
                              period: taskPeriod,
                              timeSlots: [],
                            }));
                          }

                          setSlotTime(event.target.value as TimeSlotType);
                        }}
                        value={slotTime}
                        options={[
                          { label: translate("no"), value: TimeSlotType.NONE },
                          {
                            label: translate("fixedTimeSlot"),
                            value: TimeSlotType.FIXED,
                          },
                          {
                            label: translate("variableTimeSlot"),
                            value: TimeSlotType.VARIABLE,
                          },
                        ]}
                      />
                    </Grid>
                  )}
                </Grid>
                {!isMultiDayTask && (
                  <React.Fragment>
                    <Grid item container xs={12} spacing={2}>
                      <Grid item sm={12} md={3}>
                        <Select
                          required
                          label={translate("period")}
                          placeholder={translate("select")}
                          value={task.period}
                          onChange={(event: SelectChangeEvent<any>) =>
                            handlePeriodChange({ period: event.target.value, timezone })
                          }
                          renderValue={() =>
                            getDateRangeValue(task.period ?? TaskStartDatePeriod.MORNING)
                          }
                        >
                          <PeriodMenuItem value={TaskStartDatePeriod.MORNING}>
                            <MenuItemTitle>{translate("morning")}</MenuItemTitle>
                            {getStartDateRangeLabel(defaultTimePeriods.morning)}
                          </PeriodMenuItem>
                          <PeriodMenuItem value={TaskStartDatePeriod.AFTERNOON}>
                            <MenuItemTitle>{translate("afternoon")}</MenuItemTitle>
                            {getStartDateRangeLabel(defaultTimePeriods.afternoon)}
                          </PeriodMenuItem>
                          <PeriodMenuItem value={TaskStartDatePeriod.EVENING}>
                            <MenuItemTitle>{translate("night")}</MenuItemTitle>
                            {getStartDateRangeLabel(defaultTimePeriods.evening, {
                              considerEvening: true,
                            })}
                          </PeriodMenuItem>
                          <PeriodMenuItem value={TaskStartDatePeriod.FULL_DAY}>
                            <MenuItemTitle>{translate("fullDay")}</MenuItemTitle>
                            {getStartDateRangeLabel(defaultTimePeriods.fullDay, {
                              isFullDay: true,
                            })}
                          </PeriodMenuItem>
                          <MenuItem value={TaskStartDatePeriod.CUSTOM}>
                            {translate("customTime")}
                          </MenuItem>
                        </Select>
                      </Grid>
                      <Grid item sm={12} md={3}>
                        <Input
                          required
                          data-cy="formInputDateTask"
                          type="date"
                          min={new Date().toISOString().split("T")[0]}
                          label={translate("date")}
                          value={startDateLabel}
                          error={fieldHasError("date")}
                          errorMessage={getErrorByField("date")}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            handleChangeDateTime("startDate", event.target.value);
                          }}
                        />
                      </Grid>
                      <Grid item sm={12} md={3}>
                        <Input
                          data-cy="formInputTimeTask"
                          type="time"
                          name="time"
                          required
                          disabled={task.period !== TaskStartDatePeriod.CUSTOM}
                          label={translate("from")}
                          value={startTime}
                          onChange={(event) => handleChangeTime("startDate", event.target.value)}
                          error={fieldHasError("startTime")}
                          errorMessage={getErrorByField("startTime")}
                        />
                      </Grid>
                      <Grid item sm={12} md={3}>
                        <Input
                          type="time"
                          required
                          disabled={task.period !== TaskStartDatePeriod.CUSTOM}
                          label={translate("to")}
                          placeholder="00:00 AM"
                          value={endTime}
                          onChange={(event) => handleChangeTime("endDate", event.target.value)}
                          error={fieldHasError("endTime")}
                          errorMessage={getErrorByField("endTime")}
                        />
                      </Grid>
                    </Grid>
                  </React.Fragment>
                )}
                {isMultiDayTask && (
                  <Grid item container xs={12} spacing={2}>
                    {slotTime !== TimeSlotType.VARIABLE && (
                      <>
                        <Grid item xs={slotTime === TimeSlotType.FIXED ? 3 : 6}>
                          <Input
                            type="date"
                            required
                            label={translate("startDate")}
                            placeholder=""
                            min={new Date().toISOString().split("T")[0]}
                            value={startDateLabel}
                            onChange={(event) => {
                              handleChangeDateTime("startDate", event.target.value);
                            }}
                            disabled={!isMultiDayTask}
                            error={fieldHasError("startDate")}
                            errorMessage={getErrorByField("startDate")}
                          />
                        </Grid>
                        <Grid item xs={slotTime === TimeSlotType.FIXED ? 3 : 6}>
                          <Input
                            type="date"
                            required
                            label={translate("endDate")}
                            min={add(new Date(task.startDate), { days: 1 })}
                            placeholder=""
                            value={endDateLabel}
                            onChange={(event) => {
                              handleChangeDateTime("endDate", event.target.value);
                            }}
                            disabled={!isMultiDayTask}
                            error={fieldHasError("endDate")}
                            errorMessage={getErrorByField("endDate")}
                          />
                        </Grid>
                        {slotTime === TimeSlotType.FIXED && (
                          <>
                            <Grid item xs={3}>
                              <Input
                                required
                                type="time"
                                label={translate("from")}
                                value={startTime}
                                onChange={(event) =>
                                  handleChangeTime("startDate", event.target.value)
                                }
                                error={fieldHasError("startTime")}
                                errorMessage={getErrorByField("startTime")}
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <Input
                                required
                                type="time"
                                label={translate("to")}
                                value={endTime}
                                onChange={(event) =>
                                  handleChangeTime("endDate", event.target.value)
                                }
                                error={fieldHasError("endTime")}
                                errorMessage={getErrorByField("endTime")}
                              />
                            </Grid>
                          </>
                        )}
                      </>
                    )}
                    {slotTime === TimeSlotType.VARIABLE && (
                      <VariableSlotTime
                        task={task}
                        setTask={(item) =>
                          setTask((prev) => ({ ...(prev || {}), ...(item || {}) }))
                        }
                        errors={errors}
                        currentTimezone={timezone}
                      />
                    )}
                  </Grid>
                )}

                {!assignToAgent && (
                  <Grid item container xs={12} spacing={2}>
                    <Grid item sm={12} md={3}>
                      <SkeletonComponent isLoading={isLoadingTeams} classesName={classes.skeleton}>
                        <Select
                          label={translate("team")}
                          placeholder={translate("select")}
                          value={task?.teamId || ""}
                          onChange={(event: SelectChangeEvent<any>) =>
                            handleChangeTeam(event.target.value)
                          }
                        >
                          {teams?.map((team) => (
                            <MenuItem key={team.id} value={team.id}>
                              {team.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </SkeletonComponent>
                    </Grid>

                    <Grid item sm={12} md={3}>
                      <SkeletonComponent
                        isLoading={isLoadingMembers}
                        classesName={classes.skeleton}
                      >
                        <AutoSuggest
                          data-cy="formAutosuggestUser"
                          label={translate("leader")}
                          placeholder={translate("select")}
                          error={fieldHasError("memberLeaderId")}
                          errorMessage={getErrorByField("memberLeaderId")}
                          getOptionLabel={getMemberDescription}
                          getOptionSelected={(option: Member, value: Member) =>
                            option.id === value.id
                          }
                          options={memberLeaders}
                          onChange={(member: Member) => {
                            updateTaskField("memberLeader", member);
                            updateTaskField("memberLeaderId", member?.id);
                          }}
                          value={task?.memberLeader ?? null}
                        />
                      </SkeletonComponent>
                    </Grid>
                    <Grid item sm={12} md={3}>
                      <SkeletonComponent
                        isLoading={isLoadingMembers}
                        classesName={classes.skeleton}
                      >
                        <AutoSuggest
                          dataCy="autosuggestUserWithBadge"
                          label={translate("members")}
                          placeholder={!task.members?.length ? translate("select") : ""}
                          getOptionLabel={getMemberDescription}
                          getOptionSelected={(option: Member, value: Member) =>
                            option?.id === value?.id
                          }
                          value={task.members ?? []}
                          multiple
                          onChange={(members: Member[]) => updateTaskField("members", members)}
                          options={members}
                        />
                      </SkeletonComponent>
                    </Grid>
                    <Grid item sm={12} md={3}>
                      <SkeletonComponent
                        isLoading={isLoadingFleets && hasCompanyAndBranchIdEnabled}
                        classesName={classes.skeleton}
                      >
                        <AutoSuggest
                          dataCy="autosuggestFleetWithBadge"
                          label={translate("equipment")}
                          placeholder={task?.fleets?.length ? "" : translate("select")}
                          getOptionLabel={(fleet?: Fleet) => {
                            if (fleet) {
                              const { fullText } = getEquipmentName(
                                fleet,
                                translate("general"),
                                translate("fleet")
                              );

                              return fullText;
                            }

                            return "";
                          }}
                          getOptionSelected={(option: Fleet, value: Fleet) =>
                            option?.id === value?.id
                          }
                          value={task?.fleets ?? []}
                          multiple
                          onChange={(fleets: Fleet[]) => updateTaskField("fleets", fleets)}
                          options={fleets ?? []}
                        />
                      </SkeletonComponent>
                    </Grid>
                  </Grid>
                )}
                {assignToAgent && (
                  <Grid item sm xs={12}>
                    <div className={classes.selectAnAgentInfo}>
                      <div className={classes.selectAnAgentInfoIcon}>
                        <InfoOutlinedIcon sx={{ fontSize: 20 }} />
                      </div>
                      <div className={classes.selectAnAgentInfoText}>
                        {translate("externalAgentAssignmentInfo")}
                      </div>
                    </div>
                  </Grid>
                )}
              </Grid>
              <Grid container direction="row" justifyContent="flex-start">
                <Grid item sm md="auto" lg="auto">
                  <AddTaskButton
                    data-cy="formButtonAddTask"
                    startIcon={<AiOutlinePlus />}
                    onClick={() => handleAddTask()}
                    customColor={Color.SECONDARY_DARK}
                  >
                    {translate("addTask")}
                  </AddTaskButton>
                </Grid>
              </Grid>
            </section>
          )}

        <div data-cy="addTaskList" className={classes.containerTasks}>
          {renderTasks()}
        </div>

        {!isOnViewMode && (
          <div className={classes.buttons}>
            <ActionButtons
              cancelDisabled={isUpdating}
              savePublishDisabled={isUpdating}
              saveDisabled={!!isUpdating}
              onCancel={onCancel}
              onSave={onSave}
              onSavePublish={onSavePublish}
            />
          </div>
        )}
      </div>
      <LoadingModal showing={isUpdating} />
    </div>
  );
};

export default TasksTab;
